import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ComponentRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  finalize,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';

import { DprDetailsGridContainerBaseComponent } from '@dpr-details/models/component';
import { DprDetailsService } from '@dpr-details/services';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ExceptionMessage, StaticMessage } from '@shared/enums';
import { DprAttachment, DprHidden } from '@shared/models/dpr';
import { Sync } from '@shared/models/grid';
import { HiddenSubtab } from '@shared/models/project-setup';
import { MergeService, ScreenSizeService } from '@shared/services';
import { SyncService } from '@shared/services/grid';
import {
  DprHiddenGridSyncResult,
  WriteDprHidden,
} from '@dpr-details/models/sync';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { GridSync } from '@grid/models';
import { DprCommentAttachmentsComponent } from '@dpr-details/components';
import { DprAttachmentService } from '@shared/services/dpr';

@UntilDestroy()
@Component({
  selector: 'app-dpr-hidden-page',
  templateUrl: './dpr-hidden-page.component.html',
  styleUrls: ['./dpr-hidden-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprHiddenPageComponent
  extends DprDetailsGridContainerBaseComponent<DprAttachmentsGridData>
  implements OnInit
{
  hiddenTitle = $localize`:@@dprDetails.dprHiddenPage.titleHidden:Hidden free text fields`;
  preselectedItem: string | number;

  ExceptionMessage = ExceptionMessage;
  StaticMessage = StaticMessage;

  private dprHiddenData = new Map<number, WriteDprHidden>();
  private activatedComponent$ = new BehaviorSubject<ComponentRef<any>>(null);
  private pageRoutePath: string;

  constructor(
    private router: Router,
    private dprAttachmentService: DprAttachmentService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    private syncService: SyncService,
    private mergeService: MergeService,
    protected activatedRoute: ActivatedRoute,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute);
  }

  private get subTab(): number {
    const parts = this.router.url.split('/');

    return +parts[parts.length - 1];
  }

  private get dprHidden(): DprHidden[] {
    // TODO(improvement): revise loading logic to be moved to a lower level.
    // until then ternary operator is needed, because the first emission of dprDetails is undefined (data is not ready yet).
    return this.dprDetails?.dprHidden;
  }

  //#region Lifecycle hooks

  ngOnInit(): void {
    super.ngOnInit();

    this.pageRoutePath = this.activatedRoute.snapshot.routeConfig.path;

    this.selectedSubproject$
      .pipe(
        untilDestroyed(this),
        tap((selectedSubproject) => {
          const hiddenSubtabs = selectedSubproject.data?.hiddenSubtabs;
          const hiddenTabName =
            this.selectedSubproject.data.subprojectConfiguration.customNames
              .hiddenTabName;

          if (hiddenTabName) {
            this.hiddenTitle = hiddenTabName;
          }

          const selectedTab = hiddenSubtabs.find((x) => x.id === this.subTab);

          this.preselectedItem = this.subTab;

          // This is necessary in order to automatically redirect to the first tab if its missing from the url
          //  or to the base page if the selected tab does not exist in the list
          if (!selectedTab) {
            this.router.navigate(
              hiddenSubtabs?.length ? [hiddenSubtabs[0].id] : ['./'],
              {
                relativeTo: this.activatedRoute,
                queryParamsHandling: 'merge',
                replaceUrl: true,
              }
            );
          }
        })
      )
      .subscribe();

    this.dprDetailsOfflineChanges$.pipe(untilDestroyed(this)).subscribe();

    this.activatedComponent$
      .pipe(
        untilDestroyed(this),
        filter((activatedComponent) => !!activatedComponent),
        tap((activatedComponent) => {
          this.onActivatedComponentChanged(activatedComponent);
        })
      )
      .subscribe();

    this.dprDetails$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          // this is needed to refresh the current component when data is reloaded
          this.setupRouteComponent(this.subTab);

          this.refreshFlags();
        })
      )
      .subscribe();

    fromEvent(window, 'popstate')
      .pipe(
        untilDestroyed(this),
        tap((event) => {
          const parts = (event.target as Window).location.pathname.split('/');
          const id = +parts[parts.length - 1];
          if (parts[parts.length - 2] === this.pageRoutePath && !!id) {
            this.storeComponentChanges();
            this.setupRouteComponent(id);
          }
        })
      )
      .subscribe();
  }

  //#endregion

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    this.storeComponentChanges();

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges();

    const saveChanges$ = validateChanges$.pipe(
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(isAutoSyncFromAdminChange);
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onComponentActivated(component: ComponentRef<any>): void {
    this.activatedComponent$.next(component);

    const dprHiddenComponent =
      component as unknown as DprCommentAttachmentsComponent;

    dprHiddenComponent.dataChanged.pipe(untilDestroyed(this)).subscribe(() => {
      this.isDirty$.next(true);
    });

    dprHiddenComponent.gridRegistered
      .pipe(untilDestroyed(this))
      .subscribe((gridApi) => {
        this.gridApi = gridApi;
      });
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
    this.storeComponentChanges();
    this.gridApi?.reset();
    this.setupRouteComponent(+currentSelection);
  }

  onSubtabChanged(subtabId: number): void {
    this.preselectedItem = subtabId;
    this.storeComponentChanges();
    this.gridApi?.reset();
    this.setupRouteComponent(subtabId);
  }

  trackHiddenSubtabs(_index: number, hiddenSubtab: HiddenSubtab): number {
    return hiddenSubtab?.id;
  }

  //#region Protected methods

  protected refreshData(): void {
    this.dprHiddenData.clear();

    this.refreshFlags();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  //#endregion

  //#region Private methods

  private refreshFlags(): void {
    this.gridApi?.reset();

    if (this.activatedComponent instanceof DprCommentAttachmentsComponent) {
      this.activatedComponent.gridHasChanges$.next(false);
    }

    this.isDirty$.next(false);
  }

  private validateChanges(): Observable<boolean> {
    let isValid = true;
    this.dprHiddenData.forEach((data) => {
      const syncGridData = this.mapToDprAttachmentsGridData(
        data.syncDprHiddenAttachments
      );

      if (!this.dprAttachmentService.validateChanges(syncGridData)) {
        isValid = false;
        return;
      }
    });

    return of(isValid);
  }

  private saveChanges(
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprHiddenGridSyncResult> {
    const syncDprHidden = this.buildDprHiddenSync();

    const syncData = this.dprDetailsSyncModelBuilderService.forDprHidden(
      this.selectedSubproject.id,
      this.dprDetails.id,
      syncDprHidden
    );

    const saveChanges$ = this.dprDetailsService
      .syncDprHidden(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => this.onSaveSuccess(result),
      error: (error: HttpErrorResponse) =>
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.hiddenTabName,
          this.selectedSubproject.id,
          error
        ),
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprHiddenGridSyncResult): void {
    this.refreshData();

    this.offlineFacade.setDprHidden(
      result.dprHidden,
      result.firstWarning,
      result.warningStatusTab,
      result.dprDetailId
    );
  }

  private onActivatedComponentChanged(component: ComponentRef<any>): void {
    this.storeComponentChanges();
    this.gridApi?.reset();

    this.activatedComponent = component;

    this.setupRouteComponent(this.subTab);
  }

  private storeComponentChanges(): void {
    if (!this.activatedComponent) {
      return;
    }

    const currentHiddenTab: WriteDprHidden = this.dprHidden.find(
      (x) => x.hiddenSubtabId === this.subTab
    ) ?? {
      id: uuid(),
      comment: '',
      dprDetailId: this.dprDetails.id,
      hiddenSubtabId: this.subTab,
      dprHiddenAttachments: [],
      syncDprHiddenAttachments: this.syncService.getDefaultSyncModel(),
    };

    const component = this
      .activatedComponent as unknown as DprCommentAttachmentsComponent;

    if (
      currentHiddenTab?.comment !==
        component.commentComponent.commentFormControl.value ||
      this.gridApi.hasChanges()
    ) {
      const currentAttachmentsChanges = this.gridApi.getChanges();

      this.dprHiddenData.set(this.subTab, {
        ...currentHiddenTab,
        comment: component.commentComponent.commentFormControl.value?.trim(),
        dprHiddenAttachments: this.dprAttachmentService
          .mapToAttachments(component.gridData)
          ?.map((x) => ({ ...x, dprHiddenId: currentHiddenTab.id })),
        syncDprHiddenAttachments: this.mapToDprHiddenAttachment(
          currentAttachmentsChanges,
          currentHiddenTab.id
        ),
      });
    }
  }

  private setupRouteComponent(id: number): void {
    if (!this.activatedComponent) {
      return;
    }

    const storedSubTabData = this.dprHiddenData.get(id);
    const dprDetailsHiddenData = this.dprHidden?.find(
      (x) => x.hiddenSubtabId === id
    );

    const comment = (storedSubTabData ?? dprDetailsHiddenData)?.comment ?? '';

    const attachmentsGridData =
      this.dprAttachmentService.mapToGridData(
        storedSubTabData?.dprHiddenAttachments ??
          dprDetailsHiddenData?.dprHiddenAttachments
      ) ?? [];

    const storedGridDataSync = this.mapToDprAttachmentsGridData(
      storedSubTabData?.syncDprHiddenAttachments
    );
    const attachmentsChanges: GridSync<DprAttachmentsGridData> = {
      ...storedGridDataSync,
      addedList:
        storedGridDataSync?.addedList ??
        dprDetailsHiddenData?.syncDprHiddenAttachments?.addedList ??
        [],
      updatedList:
        storedGridDataSync?.updatedList ??
        dprDetailsHiddenData?.syncDprHiddenAttachments?.updatedList ??
        [],
      deletedIds:
        storedGridDataSync?.deletedIds ??
        dprDetailsHiddenData?.syncDprHiddenAttachments?.deletedIds ??
        [],
    };

    const component = this
      .activatedComponent as unknown as DprCommentAttachmentsComponent;

    component.gridData = attachmentsGridData;
    component.predefinedChanges = attachmentsChanges;
    component.canEdit = this.canEdit;
    component.selectedSubproject = this.selectedSubproject;
    component.comment = comment;
    component.clientId = this.selectedClientId;
    component.isAppOnline = this.isAppOnline;
    component.shouldDisplayPrintInReport = false;
    component.commentComponent.commentFormControl.setValue(comment);

    this.setupCanEditBinding();
    this.setupIsAppOnlineBinding();
  }

  private buildDprHiddenSync(): Sync<DprHidden> {
    const changes = this.syncService.getDefaultSyncModel<DprHidden>();

    Array.from(this.dprHiddenData.values())
      .filter((x) => !!x)
      .forEach((dprHidden) => {
        !isNaN(+dprHidden.id)
          ? changes.updatedList.push(dprHidden)
          : changes.addedList.push(dprHidden);
      });

    const offlineChanges =
      cloneDeep(this.dprDetailsOfflineChanges?.dprHidden) ??
      this.syncService.getDefaultSyncModel();
    return this.mergeService.mergeChanges(offlineChanges, changes);
  }

  private mapToDprHiddenAttachment(
    changes: GridSync<DprAttachmentsGridData>,
    dprHiddenId: string | number
  ): GridSync<DprAttachment> {
    return {
      ...changes,
      addedList:
        this.dprAttachmentService
          .mapToAttachments(changes?.addedList)
          ?.map((x) => ({ ...x, dprHiddenId })) ?? [],
      updatedList:
        this.dprAttachmentService
          .mapToAttachments(changes?.updatedList)
          ?.map((x) => ({ ...x, dprHiddenId })) ?? [],
      deletedIds: changes.deletedIds ?? [],
    };
  }

  private mapToDprAttachmentsGridData(
    changes: GridSync<DprAttachment>
  ): GridSync<DprAttachmentsGridData> {
    return {
      ...changes,
      addedList: this.dprAttachmentService.mapToGridData(changes?.addedList),
      updatedList: this.dprAttachmentService.mapToGridData(
        changes?.updatedList
      ),
    };
  }

  //#endregion
}
